import { HECTO, KILO } from 'constants/metricPrefixes'
import {
  OrderCopy,
  OrderCopyCargoBySeparate,
  OrderCopyCargoByTotal,
  OrderCopyCustomer,
  OrderCopyOperation,
  OrderCopyPartner,
  OrderCopyPoint,
  OrderCreateCargo,
  OrderCreateCargoTotal,
  OrderCreateCustomer,
  OrderCreateRequest
} from 'schema'
import { div } from 'shared/helpers/number'
import { OrderAddStore } from 'store/orderAdd/types'

import { DEFAULT_CARGO_ITEM } from '../../components/OrderCreate/constants'
import { OrderCreateOperationForm } from '../../components/OrderCreatePointOperation/types'
import {
  RadioSwitchValue,
  TOrderCreateCargoForm,
  TOrderCreateFormData,
  TOrderCreatePartnerFormData,
  TOrderCreatePoint
} from '../../types/orderCreateTypes'

const transformCustomer = (customer: OrderCopyCustomer) => {
  const { company, type } = customer

  return {
    company: {
      name: company?.name || '',
      inn: company?.inn,
      kpp: company?.kpp
    },
    customerId: company?.id || '',
    type
  }
}

const convertUnitToCm = (value = 0) => Number(div(value, HECTO))

const convertUnitToKilogram = (value = 0) => Number(div(value, KILO))

const transformCargoSeparate = (cargoBySeparate: OrderCopyCargoBySeparate[] | null) =>
  (cargoBySeparate || []).reduce((previousValue, currentValue) => {
    let item
    const { width, length, height, weight } = currentValue
    const type = currentValue.type as unknown as OrderCreateCargo.type

    if (type === OrderCreateCargo.type.CUSTOM) {
      item = {
        ...currentValue,
        customLength: length,
        customWidth: width,
        customHeight: height,
        customWeight: convertUnitToKilogram(weight)
      } as OrderCreateCargo
    }

    if (type === OrderCreateCargo.type.PALLET) {
      item = {
        ...currentValue,
        palletLength: length,
        palletWidth: width,
        palletHeight: height,
        palletWeight: convertUnitToKilogram(weight)
      } as OrderCreateCargo
    }

    if (item) {
      previousValue.push(item)
    }

    return previousValue
  }, [] as OrderCreateCargo[])

const transformCargoTotal = (cargoByTotal: OrderCopyCargoByTotal | null): OrderCreateCargoTotal => {
  const { width, height, length, weight, about, amount, price, volume, maxWeight } = cargoByTotal || {}
  const cargoTotal: OrderCreateCargoTotal = {
    amount: Number(amount),
    about: about || '',
    price: String(price),
    volume: Number(volume),
    height: convertUnitToCm(height),
    width: convertUnitToCm(width),
    length: convertUnitToCm(length),
    weight: convertUnitToKilogram(weight),
    type: OrderCopyCargoByTotal.type.CUSTOM,
    maxWeight: convertUnitToKilogram(maxWeight || undefined)
  }

  return cargoTotal
}

const transformPartner = (partner: OrderCopyPartner): TOrderCreatePartnerFormData | undefined => {
  if (!partner.contact && !partner.isUser) {
    return undefined
  }

  return {
    isUser: partner.isUser ? RadioSwitchValue.yes : RadioSwitchValue.no,
    companyId: partner?.company?.id,
    company: partner?.company || undefined,
    contactId: partner?.contact?.id,
    contact: partner?.contact || undefined,
    type: partner?.type
  }
}

const transformPoint = (point: OrderCopyPoint): TOrderCreatePoint => {
  const { address, addressDetail, isCourier, terminal } = point

  if (!addressDetail) {
    return {
      terminal,
      terminalId: terminal.id,
      addressDetail: {
        address,
        comment: undefined,
        fiasId: '',
        latitude: terminal.latitude,
        longitude: terminal.longitude
      },
      isCourier
    } as TOrderCreatePoint
  }

  return {
    addressDetail: addressDetail || undefined,
    terminalId: terminal?.id || '',
    isCourier: Boolean(isCourier),
    terminal
  } as TOrderCreatePoint
}

const transformOperation = (
  operations: OrderCopyOperation[] | null,
  departureTerminalId?: string,
  destinationTerminalId?: string
): OrderCreateOperationForm[] => {
  if (!operations) {
    return []
  }

  const sameTerminalCounts = new Map<string, number>()

  return operations.map((operation) => {
    const base = {
      operationId: operation.operationId,
      name: operation.name,
      amount: operation.amount || 1
    }

    if (!operation.terminalId) {
      return base
    }

    let pointType: 'departure' | 'destination' | undefined

    if (operation.terminalId === departureTerminalId && operation.terminalId === destinationTerminalId) {
      const count = sameTerminalCounts.get(operation.operationId) ?? 0
      sameTerminalCounts.set(operation.operationId, count + 1)
      pointType = count % 2 === 0 ? 'departure' : 'destination'
    } else if (operation.terminalId === departureTerminalId) {
      pointType = 'departure'
    } else if (operation.terminalId === destinationTerminalId) {
      pointType = 'destination'
    }

    return {
      ...base,
      terminalId: operation.terminalId,
      pointType
    }
  })
}

export const transform = (order: OrderCopy): TOrderCreateFormData => {
  const { cargoInfo, comment, customer, departure, destination, operation } = order

  const cargoForm = (cargoInfo.separate
    ? transformCargoSeparate(cargoInfo.separate)
    : ([DEFAULT_CARGO_ITEM] as OrderCreateCargo[])) as unknown as TOrderCreateCargoForm[]
  const cargoTotalForm = cargoInfo.total ? transformCargoTotal(cargoInfo.total) : undefined
  const cargoTypeForm = cargoInfo.type as unknown as OrderCreateRequest.cargoType
  const customerForm = customer ? transformCustomer(customer) : undefined
  const departurePartnerForm = transformPartner(departure.partner)
  const destinationPartnerForm = transformPartner(destination.partner)
  const departurePointForm = departure.point ? transformPoint(departure.point) : undefined
  const destinationPointForm = destination.point ? transformPoint(destination.point) : undefined
  const operationForm = transformOperation(operation, departure.point?.terminal.id, destination.point?.terminal.id)

  return {
    cargo: cargoForm,
    cargoTotal: cargoTotalForm,
    cargoType: cargoTypeForm,
    comment: comment || undefined,
    customer: customerForm,
    departureDate: undefined,
    departurePartner: departurePartnerForm,
    departurePoint: departurePointForm,
    destinationPartner: destinationPartnerForm,
    destinationPoint: destinationPointForm,
    payer: order?.payer || undefined,
    operation: operationForm
  }
}

export const filterAvailableOperations = (
  operations: OrderCreateOperationForm[],
  available: { common: Set<string>; departure: Set<string>; destination: Set<string> },
  departureTerminalId?: string,
  destinationTerminalId?: string
): OrderCreateOperationForm[] =>
  operations.filter((op) => {
    if (!op.terminalId) {
      return available.common.has(op.operationId)
    }

    if (op.terminalId === departureTerminalId && available.departure.has(op.operationId)) {
      return true
    }

    if (op.terminalId === destinationTerminalId && available.destination.has(op.operationId)) {
      return true
    }

    return false
  })

export const getFullInfoPayload = ({
  departurePoint,
  destinationPoint,
  customer
}: {
  customer?: OrderCreateCustomer
  departurePoint?: TOrderCreatePoint
  destinationPoint?: TOrderCreatePoint
}): OrderAddStore => ({
  customerId: customer?.customerId || '',
  // @ts-ignore
  fromPoint: departurePoint
    ? {
        selectedOption: { ...departurePoint.addressDetail, value: departurePoint.addressDetail?.address || '' },
        selectedTerminal: departurePoint.terminal,
        daDataSearchAddressValue: departurePoint.addressDetail?.address || '',
        isAddressCourierDelivery: departurePoint.isCourier,
        isAddressTerminalDelivery: !departurePoint.isCourier,
        isCourier: departurePoint.isCourier,
        terminals: [departurePoint.terminal],
        comment: departurePoint.addressDetail?.comment || '',
        errorAddress: ''
      }
    : {},
  // @ts-ignore
  toPoint: destinationPoint
    ? {
        selectedOption: { ...destinationPoint.addressDetail, value: destinationPoint.addressDetail?.address },
        selectedTerminal: destinationPoint.terminal,
        daDataSearchAddressValue: destinationPoint.addressDetail?.address || '',
        isAddressCourierDelivery: destinationPoint.isCourier,
        isAddressTerminalDelivery: !destinationPoint.isCourier,
        isCourier: destinationPoint.isCourier,
        terminals: [destinationPoint.terminal],
        comment: destinationPoint.addressDetail?.comment || '',
        errorAddress: ''
      }
    : {},
  isDadataDisable: false
})
